/**
 * Resolve resource slugs (answers + learn pages) into RelatedPagesGrid cards
 * for industry hub pages. Single source of truth so the Resources section on
 * every industry page links to real, internal content with real titles.
 */
import { ANSWERS } from "@/lib/answersData";

export type IndustryResourceCard = {
  to: string;
  title: string;
  description: string;
};

/** Hand-maintained registry of /learn page titles + 1-line summaries we want
 *  to surface from industry hubs. Keys are the slug after `learn.`. */
const LEARN_PAGES: Record<string, { title: string; description: string }> = {
  "local-seo-guide": {
    title: "Local SEO guide for Colorado businesses",
    description:
      "How local SEO actually works in 2026 — Map Pack, GBP, reviews, and on-page signals that move the needle.",
  },
  "google-business-profile": {
    title: "Google Business Profile playbook",
    description:
      "Step-by-step GBP setup, categories, services, photos, and posts that win local searches.",
  },
  "google-maps-ranking-colorado": {
    title: "How to rank in Google Maps in Colorado",
    description:
      "What it really takes to crack the Map Pack in Colorado Springs, Denver, and the Front Range.",
  },
  "google-ads-vs-seo-colorado": {
    title: "Google Ads vs SEO in Colorado",
    description:
      "When to spend on ads, when to compound with SEO, and how to run both without wasting budget.",
  },
  "get-more-reviews": {
    title: "Get more Google reviews (the right way)",
    description:
      "A simple, compliant review system that actually grows your star count and Map Pack ranking.",
  },
  "marketing-budget": {
    title: "Realistic monthly marketing budget",
    description:
      "What Colorado small businesses should actually spend on SEO, ads, and web each month.",
  },
  "seo-pricing-colorado": {
    title: "SEO pricing in Colorado — explained",
    description:
      "What honest SEO costs in Colorado, what's a red flag, and what you should expect for the money.",
  },
  "web-design-cost-colorado": {
    title: "Web design cost in Colorado",
    description:
      "What a real, fast, mobile-first website costs in Colorado — and what's hiding in cheap quotes.",
  },
  "website-conversion": {
    title: "Website conversion essentials",
    description:
      "The handful of things on a small-business website that actually turn visits into calls.",
  },
  "what-is-geo-ai": {
    title: "What is GEO AI?",
    description:
      "How Generative Engine Optimization gets your business cited inside ChatGPT, Gemini, and AI Overviews.",
  },
  "geo-ai-vs-seo": {
    title: "GEO AI vs SEO",
    description:
      "How AI search changes the local marketing playbook — and what still works exactly the same.",
  },
  "ai-search-geo": {
    title: "AI search & GEO explained",
    description:
      "Plain-English breakdown of how AI search engines decide which local businesses to recommend.",
  },
  "service-area-pages": {
    title: "Service area pages that rank",
    description:
      "Why thin service-area pages tank rankings — and how to build ones Google actually trusts.",
  },
  "mobile-website-mistakes": {
    title: "Mobile website mistakes that kill leads",
    description:
      "The fixable mobile UX mistakes costing Colorado businesses real phone calls every week.",
  },
  "how-long-does-local-seo-take": {
    title: "How long does local SEO take?",
    description:
      "Realistic timelines for Map Pack movement, organic traffic, and lead flow in competitive Colorado markets.",
  },
};

/** Build a single resource card from a slug. Slugs starting with `learn:` resolve
 *  to a /learn page, everything else is treated as an /answers slug. */
export function resolveResource(slug: string): IndustryResourceCard | null {
  if (slug.startsWith("learn:")) {
    const key = slug.slice("learn:".length);
    const meta = LEARN_PAGES[key];
    if (!meta) return null;
    return { to: `/learn/${key}`, title: meta.title, description: meta.description };
  }
  const answer = ANSWERS.find((a) => a.slug === slug);
  if (!answer) return null;
  return {
    to: `/answers/${answer.slug}`,
    title: answer.question,
    description: answer.shortAnswer.length > 160
      ? answer.shortAnswer.slice(0, 157).trimEnd() + "…"
      : answer.shortAnswer,
  };
}

export function resolveResources(slugs: readonly string[]): IndustryResourceCard[] {
  return slugs
    .map(resolveResource)
    .filter((c): c is IndustryResourceCard => c !== null);
}
